import {
  type Collision,
  type CollisionDetection,
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  DragOverlay,
  type DragStartEvent,
  type DroppableContainer,
  getFirstCollision,
  type Over,
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import {
  createContext,
  type PointerEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { useSwimlaneEventPublisher, useSwimlanePrivateApi } from '../../api';
import { useSwimlaneState } from '../../context';
import type { ICardProps, TCardId, TCellId } from '../../types';
import { buildCellId, parseCardId, parseCellId } from '../../utils';
import { GRID_GAP, SORTABLE_TYPES } from '../constants';

import { DraggingCardsPreview } from './DraggingCardsPreview';

type TDraggingCardIds = Set<TCardId>;

const EMPTY_DRAGGING_CARD_IDS: TDraggingCardIds = new Set<TCardId>();
const DragAboveCenterContext = createContext(false);
const getCardDropPosition = (over: Over, collisions: Collision[]) => {
  const collisionObject = collisions.find((collision) => collision.id === over.id);

  let top = over.rect.top + 20;

  if (over.data.current?.type === SORTABLE_TYPES.Cell && collisionObject) {
    const collisionNode = collisionObject?.data?.droppableContainer.node.current;
    if (collisionNode?.firstChild) {
      top = collisionNode.lastChild.getBoundingClientRect().bottom + 20;
    }
  }

  return {
    top,
    left: over.rect.left + over.rect.width / 2, // position in center of the item
  };
};

const CardsDNDContext = createContext<TDraggingCardIds>(EMPTY_DRAGGING_CARD_IDS);

type CardsDNDContextProviderProps = {
  children: ReactNode;
  onDragEnd?: () => void;
};

function shouldHandleEvent(element: HTMLElement | null) {
  let cur = element;
  while (cur) {
    if (cur.dataset?.noDnd) {
      return false;
    }
    cur = cur.parentElement;
  }
  return true;
}

// Custom PointerSensor to prevent dragging when the element has data-no-dnd attribute
class CustomPointerSensor extends PointerSensor {
  static override activators = [
    {
      eventName: 'onPointerDown' as const,
      handler: ({ nativeEvent: event }: PointerEvent) => {
        return shouldHandleEvent(event.target as HTMLElement);
      },
    },
  ];
}

export const CardsDNDContextProvider = ({ children, onDragEnd }: CardsDNDContextProviderProps) => {
  const sensors = useSensors(
    useSensor(CustomPointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
  );

  const [draggingCardIds, setDraggingCardIds] = useState<TDraggingCardIds>(EMPTY_DRAGGING_CARD_IDS);
  const draggingCardIdsRef = useRef<TDraggingCardIds>(EMPTY_DRAGGING_CARD_IDS);
  const [isAboveCenter, setIsAboveCenter] = useState(false);
  const isAboveCenterRef = useRef(false);
  const privateApiRef = useSwimlanePrivateApi();
  const publishEvent = useSwimlaneEventPublisher();
  const { getCardHeight, getCellCardIDs } = useSwimlaneState();
  const getCardHeightRef = useRef(getCardHeight);
  const getCellCardIDsRef = useRef(getCellCardIDs);

  useEffect(() => {
    getCardHeightRef.current = getCardHeight;
  }, [getCardHeight]);

  useEffect(() => {
    getCellCardIDsRef.current = getCellCardIDs;
  }, [getCellCardIDs]);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;
      const { current } = active.data;
      const { id } = active;

      if (current?.type === SORTABLE_TYPES.Card && id) {
        const activeId = id as TCardId;
        const selectedCardIds = privateApiRef.current.getSelectedCardIds();
        const cardIds = new Set(
          selectedCardIds.has(activeId) ? [activeId, ...selectedCardIds] : [activeId],
        );

        setDraggingCardIds(cardIds);
        draggingCardIdsRef.current = cardIds;
      }
    },
    [privateApiRef],
  );

  const clearDraggingCardIds = useCallback(() => {
    setDraggingCardIds(EMPTY_DRAGGING_CARD_IDS);
    draggingCardIdsRef.current = EMPTY_DRAGGING_CARD_IDS;
    isAboveCenterRef.current = false;
    setIsAboveCenter(false);
  }, []);

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    const { over } = event;
    let newIsAbove = false;
    if (over?.data.current?.type === SORTABLE_TYPES.Card) {
      const activatorY = (event.activatorEvent as globalThis.PointerEvent).clientY;
      const currentY = activatorY + (event.delta?.y ?? 0);
      newIsAbove = currentY < over.rect.top + over.rect.height / 2;
    }
    if (isAboveCenterRef.current !== newIsAbove) {
      isAboveCenterRef.current = newIsAbove;
      setIsAboveCenter(newIsAbove);
    }
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const cardIds = draggingCardIdsRef.current;
      clearDraggingCardIds();

      const { active, over, collisions } = event;

      // Edge Case: if the active and over are the same, we don't need to do anything
      if (!over || active.id === over.id) {
        return;
      }

      const targetType = over.data.current?.type;

      // Edge Case: since both headers and items share the same DNDContext and can be moved, we need to check if the move is valid
      if (
        (targetType === SORTABLE_TYPES.Cell || targetType === SORTABLE_TYPES.Card) &&
        cardIds.size &&
        over.data.current?.accepts?.includes(SORTABLE_TYPES.Card)
      ) {
        const draggingCardsProps = [...cardIds].map(parseCardId);
        const firstDraggingCard = draggingCardsProps[0];

        if (!firstDraggingCard) {
          return;
        }

        const parseId = targetType === SORTABLE_TYPES.Cell ? parseCellId : parseCardId;
        const idToParse =
          targetType === SORTABLE_TYPES.Cell
            ? over.data.current?.cellId
            : over.data.current?.cardId || '';
        const cellItems =
          over.data.current?.sortable?.items || getCellCardIDsRef.current(idToParse) || [];
        const {
          rowID: targetRowID,
          columnID: targetColumnID,
          itemID,
        } = parseId(idToParse) as ICardProps;

        const lastCellCardID = cellItems[cellItems.length - 1];

        // Determine if the dragged card is above the center of the hovered card.
        // When above, treat the hovered card as the "next" (insert before it).
        const activatorY = (event.activatorEvent as globalThis.PointerEvent).clientY;
        const currentY = activatorY + (event.delta?.y ?? 0);
        const isAboveCenter =
          targetType === SORTABLE_TYPES.Card && currentY < over.rect.top + over.rect.height / 2;

        const nonDraggingCellItems = cellItems.filter((id: string) => !cardIds.has(id));

        let prevTargetItemID: string | undefined;
        let nextTargetItemID: string | undefined;

        if (isAboveCenter) {
          const targetIndex = nonDraggingCellItems.indexOf(idToParse);
          const prevCardID = targetIndex > 0 ? nonDraggingCellItems[targetIndex - 1] : undefined;
          prevTargetItemID = prevCardID ? parseCardId(prevCardID).itemID : undefined;
          nextTargetItemID = itemID;
        } else {
          prevTargetItemID = itemID || (lastCellCardID && parseCardId(lastCellCardID).itemID);
          const targetIndex = nonDraggingCellItems.indexOf(idToParse);
          const nextTargetCardID =
            targetIndex === -1 ? undefined : nonDraggingCellItems[targetIndex + 1];
          nextTargetItemID = nextTargetCardID ? parseCardId(nextTargetCardID).itemID : undefined;
        }

        publishEvent(
          'cardsDragEnd',
          {
            draggingCardIds: cardIds,
            draggingCardsProps,
            targetRowID,
            targetColumnID,
            prevTargetItemID,
            nextTargetItemID,
            dropPosition: getCardDropPosition(over, collisions as Collision[]),
          },
          event,
        );

        // COULDDO: Check if we need to measure (eg if we rerender everything on change anyway)
        onDragEnd?.();
      }
    },
    [clearDraggingCardIds, publishEvent, onDragEnd],
  );

  const cardCollisionDetection = useCallback<CollisionDetection>((args) => {
    const { droppableContainers } = args;
    if (!droppableContainers.length) {
      return [];
    }

    const cellDroppableContainers: DroppableContainer[] = [];
    const cardDroppableContainers: Map<TCellId, DroppableContainer[]> = new Map();
    for (const container of droppableContainers) {
      if (container.data.current?.type === SORTABLE_TYPES.Cell) {
        cellDroppableContainers.push(container);
      } else if (container.data.current?.type === SORTABLE_TYPES.Card) {
        const cardId = container.data.current.cardId;
        const { rowID, columnID } = parseCardId(cardId);
        const cellId = buildCellId({ rowID, columnID });
        const containers = cardDroppableContainers.get(cellId) || [];
        containers.push(container);
        cardDroppableContainers.set(cellId, containers);
      }
    }

    // Check if the pointer is within a cell, falling back to closest cell
    // when rectIntersection misses due to stale rects from layout shifts during drag
    const activeCellCollision =
      getFirstCollision(
        rectIntersection({ ...args, droppableContainers: cellDroppableContainers }),
      ) ??
      getFirstCollision(closestCenter({ ...args, droppableContainers: cellDroppableContainers }));
    if (!activeCellCollision) {
      return [];
    }
    const activeCellId = activeCellCollision.data?.droppableContainer.data.current.cellId;

    // If the pointer is not within a cell, there is no need to check for collisions
    if (!activeCellId) {
      return [];
    }

    const activeCardDroppableContainers = cardDroppableContainers.get(activeCellId);

    // If there are no cards in the cell, we only need to check the cell
    if (!activeCardDroppableContainers?.length) {
      return [activeCellCollision];
    }

    const nonDraggingCards = activeCardDroppableContainers.filter(
      (c) => !draggingCardIdsRef.current.has(c.data.current?.cardId),
    );

    const collisionTop = args.pointerCoordinates?.y ?? args.collisionRect.top;
    const containingCard = nonDraggingCards.find((c) => {
      const rect = c.rect.current;
      return rect && collisionTop >= rect.top && collisionTop <= rect.bottom;
    });
    const cardCollisions = containingCard
      ? [{ id: containingCard.id, data: { droppableContainer: containingCard, value: 0 } }]
      : closestCenter({ ...args, droppableContainers: nonDraggingCards });

    // If there are no collisions with cards, we only need to check the cell
    if (!cardCollisions?.length) {
      return [activeCellCollision];
    }

    const closestCardCollisionData = getFirstCollision(cardCollisions, 'data');
    const closestCardBottom = closestCardCollisionData?.droppableContainer.rect.current?.bottom;
    const collisionRectTop = args.collisionRect?.top;
    const draggedCardId = args.active.data.current?.cardId;
    const draggedCardHeight = getCardHeightRef.current(draggedCardId);

    // If we drag the card to the bottom of the cell, we need to check if the card is far below the last card and highlight the cell
    if (
      closestCardBottom &&
      collisionRectTop &&
      collisionRectTop > closestCardBottom + draggedCardHeight + 2 * GRID_GAP.value
    ) {
      return [activeCellCollision];
    }

    return cardCollisions;
  }, []);

  return (
    <CardsDNDContext.Provider value={draggingCardIds}>
      <DndContext
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onDragCancel={clearDraggingCardIds}
        sensors={sensors}
        collisionDetection={cardCollisionDetection}
      >
        <DragAboveCenterContext.Provider value={isAboveCenter}>
          {children}
        </DragAboveCenterContext.Provider>
        {createPortal(
          <DragOverlay modifiers={[restrictToWindowEdges]} dropAnimation={null}>
            {draggingCardIds.size && (
              <DraggingCardsPreview draggingCardIds={[...draggingCardIds]} />
            )}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
    </CardsDNDContext.Provider>
  );
};

export const useDraggingCardIds = () => useContext(CardsDNDContext);

export const useIsCardDragging = (cardId: TCardId) => useContext(CardsDNDContext).has(cardId);

export const useFirstDraggingCardId = () => useContext(CardsDNDContext).values().next().value;

export const useIsDragAboveCenter = () => useContext(DragAboveCenterContext);
